import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { Box, Button, Tooltip } from '@mui/material';
import React, { useRef, useState } from 'react';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import useFlagIssueHotkey from './useFlagIssueHotkey.hooks';
import PriorFlagsTooltip from './PriorFlagsTooltip';
import { ISSUE_TOOLTIP_Z_INDEX } from './issueReportLayers';

/**
 * HITL-1499: the affordance the whole issue-reporting pillar hangs on. One press
 * (or `Alt+I`) files a report joined to the live session, with no typing and no
 * detour off the ordering screen.
 *
 * Deliberately NOT an Intent: it lives in its own sibling group outside
 * `INTENT_SECTIONS`, so the `Alt+1…9` badge numbering — which derives from that
 * list — cannot renumber. Mis-fired ordering intents in a live drive-thru is the
 * risk being avoided.
 *
 * It has no COOLDOWN and does not fade under `isStaffInterventionHappening`, unlike
 * the intent pills: "Unnecessary Staff Intervention" is over half of all reports
 * today, so an intervention is the single moment reporting matters most. It is not a
 * TTS action either, so `intentsStatus` / `DISABLE_INTENT_INTERVAL` do not apply.
 *
 * It IS disabled for one reason and one only: a flag window is already open. That is
 * not a cooldown — it clears the moment the panel closes, which the hard cap
 * guarantees. The rule itself is enforced in the `flagIssue` thunk, because `Alt+I`
 * and HITL-1504's transcript source do not pass through this component; greying out
 * here is the VISIBLE half, so a blocked press reads as "not now" rather than as a
 * dead button.
 */
const FlagIssueButton = () => {
  // `isEnabled` is selectIsIssueReportingEnabled — hitl_3_0 AND
  // hitl_issue_reporting, ANDed in one place. Do not re-AND it here.
  const { isEnabled, flagIssue, activeReport, sessionCount, sessionReports } =
    useIssueReport();
  // Stamped when the tooltip opens so the ages hold still while they are read.
  const [tooltipOpenedAtMs, setTooltipOpenedAtMs] = useState<number | null>(
    null
  );
  // The panel is up and owns the next 10s (up to 60s if the agent is typing a
  // detail — see MAX_FLAG_WINDOW_MS). Mirrors the thunk's guard exactly: both read
  // "is there an active report", so the button and the rule cannot disagree.
  const isFlagWindowOpen = Boolean(activeReport);
  const hint = isFlagWindowOpen
    ? 'Finish the open report first'
    : 'Report an issue (Alt+I)';
  // Anchor for HITL-1500's quick-pick Popper. Created here from day one so that
  // ticket is a pure addition inside this component, with no prop plumbing
  // through IntentsSectionGrouped.
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Called BEFORE the early return — hooks cannot be conditional. It is a no-op
  // with the flag off because `flagIssue` is already gated inside useIssueReport,
  // and a no-op with a window open because the thunk drops the press. The hotkey
  // deliberately learns NEITHER rule: see the note in useFlagIssueHotkey.
  useFlagIssueHotkey(() => flagIssue({ source: 'action_bar' }));

  if (!isEnabled) return null;

  // Staff's treatment, in Staff's exact geometry and typography — solid pill,
  // uppercase, letterSpacing, weight 700, 15px outlined icon. Reporting a defect
  // has to be findable instantly mid-order, and the escalation pill is the bar's
  // established shape for "this is the one control you may need in a hurry".
  //
  // The COLOUR is deliberately not Staff's. Two red pills side by side is a real
  // misclick hazard — a mis-hit on Staff escalates to the store for real — and the
  // amber / blue slots already mean Conversation and Order Flow. Daintree sits
  // outside that scheme entirely, so the button carries Staff's weight while
  // reading as its own thing.
  //
  // Styling is `sx`, not makeStyles: MUI Button's own emotion styles override
  // makeStyles classes (same reason IntentsSectionGrouped uses sx for its pills).
  const buttonSx = {
    height: 34,
    flex: '0 0 auto',
    minWidth: 64,
    px: 1.25,
    borderRadius: '4px',
    backgroundColor: Colors.daintree,
    color: Colors.white,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 700,
    fontSize: HITL3_FONT_SIZE,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': { marginRight: '4px' },
    '&:hover': { backgroundColor: Colors.daintree },
    // MUI's default disabled treatment for a contained button is a light grey fill
    // with grey ink, which on this bar reads as BROKEN rather than as temporarily
    // unavailable — and it would drop the keycap's contrast below legible. Keep the
    // button's own colours and dim the whole thing instead, which carries the keycap
    // and the icon with it.
    '&.Mui-disabled': {
      backgroundColor: Colors.daintree,
      color: Colors.white,
      opacity: 0.45,
    },
  };

  // The `I` keycap, cloned from the intent pills' badge. A white chip inked in the
  // button's own colour — the dark-button branch of their keycapInk logic, which is
  // what Staff would use if it had a shortcut. Kept even though Staff has no keycap:
  // Alt+I is otherwise invisible, and this is the only place it is advertised in the
  // bar.
  // The running total for THIS session, rendered only from 1 up. That absence IS
  // the "reset to null, not zero" behaviour: a new session matches no records, so
  // there is no zero state to design and no reset code to get wrong.
  //
  // Inked white-on-daintree rather than the keycap's inverse, so it cannot be
  // mistaken for a second keyboard shortcut sitting next to the `I`.
  const countSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 16,
    height: 16,
    ml: 0.75,
    px: '4px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255,255,255,0.22)',
    color: Colors.white,
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: 0,
    fontVariantNumeric: 'tabular-nums',
  };

  const keycapSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 15,
    height: 15,
    px: '3px',
    mr: 0.5,
    borderRadius: '3px',
    border: `1px solid ${Colors.white}`,
    backgroundColor: Colors.white,
    color: Colors.daintree,
    fontSize: '0.62rem',
    fontWeight: 700,
    lineHeight: 1,
  };

  return (
    // The group wrapper lives HERE, not in IntentsSectionGrouped, so that with the
    // flag off this component emits nothing at all — an always-rendered empty
    // wrapper would add one more flex child and one more 8px gap to the bar on
    // every store that has the feature off.
    //
    // data-issue-report on this outermost node is what opts the whole control out
    // of agent-interception detection: the guards listen on document in the
    // capture phase and match with target.closest(), so the marker has to be an
    // ANCESTOR of whatever the real mousedown target is — the svg icon or the
    // keycap Box, not the button. Repeated on the Button itself as belt and
    // braces. Flagging a defect is supervisory, not taking the order over.
    <Box
      data-issue-report="true"
      sx={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}
    >
      {/* The tooltip has to change with the state: "Report an issue" on a button
          that cannot report one is a lie, and the disabled tooltip is the only place
          the reason is written down. The <span> wrapper is what makes a tooltip on a
          DISABLED button work at all — MUI cannot listen on the button itself once
          it stops firing pointer events.

          It also carries this session's flag history, the SAME component the panel's
          summary line uses. Two reasons it hangs off the whole button rather than
          the count badge alone, as first drawn: a tooltip on a child would fire the
          button's own tooltip too (mouseover bubbles) and the agent would get both
          at once; and the panel lives for ten seconds while hovering deliberately
          does not pause the countdown, so this is the surface that makes the history
          reachable at any point in the session, with no flag open. */}
      <Tooltip
        title={
          sessionCount > 0 ? (
            <Box>
              {/* Ruled off from the history below it. The two halves are different
                  kinds of thing — one is what this button DOES, the other is what
                  has already been reported — and without the rule they read as one
                  run-on block, the first line of which looks like a heading for the
                  rows. Same hairline the tooltip's own rows are spaced with. */}
              <Box
                data-testid="issue-tooltip-hint"
                sx={{
                  fontSize: HITL3_FONT_SIZE,
                  pb: '5px',
                  mb: '3px',
                  borderBottom: '1px solid rgba(255,255,255,0.18)',
                }}
              >
                {hint}
              </Box>
              <PriorFlagsTooltip
                reports={sessionReports}
                nowMs={tooltipOpenedAtMs ?? Date.now()}
              />
            </Box>
          ) : (
            hint
          )
        }
        onOpen={() => setTooltipOpenedAtMs(Date.now())}
        componentsProps={{
          // Same layering rule as the panel's summary line — see issueReportLayers.
          // `as never`: MUI types this slot as Partial<PopperProps>, which has
          // no `sx`, though the styled Popper it reaches does honour it.
          popper: { sx: { zIndex: ISSUE_TOOLTIP_Z_INDEX } } as never,
          tooltip: { sx: { maxWidth: 420 } },
        }}
        arrow
        disableInteractive
      >
        <span>
          <Button
            ref={buttonRef}
            onClick={() => flagIssue({ source: 'action_bar' })}
            disabled={isFlagWindowOpen}
            disableElevation
            data-issue-report="true"
            startIcon={<FlagOutlinedIcon sx={{ fontSize: 15 }} />}
            sx={buttonSx}
          >
            <Box component="span" sx={keycapSx}>
              I
            </Box>
            Issue
            {sessionCount > 0 && (
              <Box
                component="span"
                data-testid="issue-session-count"
                sx={countSx}
              >
                {sessionCount}
              </Box>
            )}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

export default FlagIssueButton;
